/**
 * JmPayContainer
 *
 *
 */

import { useEffect, useState } from 'react';
import MessageAlert, { POINT_STORE_ID } from 'utils';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import JmPayPresenter from './JmPayPresenter';
import {
  UserApi,
  JmPayApi,
  InquiryApi,
  OrderApi,
  ShopOrderApi,
  ParcelServiceApi,
} from 'api';
import {
  usePaymentCancelCashbill,
  usePaymentIssueCashbill,
} from 'hooks/PaymentHooks.';
import { useUpdateOrder } from 'hooks/OrderHooks';
import { useUpdateRefund } from 'hooks/RefundHooks';
import dayjs from 'dayjs';

const MOMENT_FORMAT = 'YYYY-MM-DD';
const endDate = moment().format(MOMENT_FORMAT);
const startDate = moment().add(-10, 'days').format(MOMENT_FORMAT);

const INITIAL_CASH_BILL = {
  mid: POINT_STORE_ID,
  orderNumb: '',
  userName: '',
  productType: 'REAL', // REAL : 실물상품 - DIGITAL : 디지털컨텐츠
  productName: '',
  issueUsageType: 'DEDUCTION', // DEDUCTION : 소득공제 - EXPENDITURE : 지출증빙
  totalAmount: 0,
  taxAmount: '',
  userInfoType: 'PHONE_NUMB', // PHONE_NUMB : 휴대폰번호 - BIZ_NUMB : 사업자번호
  userInfo: '',
  PHONE_NUMB: '',
  BIZ_NUMB: '',
};

/**----------------------------------------
 * [Component]  > 제이엠페이 - 컨테이너
 * ----------------------------------------
 */
const JmPayContainer = ({ defaultShopId, permission }) => {
  /* ===== Init ===== */
  // 오늘 날짜의 시작 (00:00:00)과 끝 (23:59:59) 타임스탬프 계산
  const { type } = useParams();

  /* ===== Mutate ===== */
  const { issueCashbill } = usePaymentIssueCashbill();
  const { cancelCashbill } = usePaymentCancelCashbill();
  const { updateOrder } = useUpdateOrder();
  const { updateRefund } = useUpdateRefund();

  /* ===== VARIABLE ===== */
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */

  /* 주문데이터 */
  const [statisticsType, _setStatisticsType] = useState(type ?? 'daily');
  const [isLoading, setIsLoading] = useState(false);
  const [currentShop, setCurrentShop] = useState(defaultShopId);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate,
    endDate,
    keyword: null,
    keywordType: 'user_name',
  });
  const [currentRow, setCurrentRow] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [userList, setUserList] = useState([]);
  const [jmpayList, setJmpayList] = useState([]);
  const [refundList, setRefundList] = useState([]);
  const [currentTab, setCurrentTab] = useState('user-list');

  const [cashBillInfo, setCashBillInfo] = useState(INITIAL_CASH_BILL);

  /* ===== FUNCTION ===== */
  /**
   * 검색
   * --
   */
  const handleSearch = (value) => {
    setDateRange(value);
  };

  /* 데이터 조회 */
  const getData = async () => {
    try {
      const { status, message, data } = await UserApi.getUsers({
        // filter: 'jm_pay_setting',
        filter: 'jm_pay',
        join_tables: `["grade"]`,
      });
      if (status !== 200) throw { message };

      if (dateRange.keyword) {
        if (dateRange.keywordType === 'user_name') {
          setUserList(
            data.filter(
              (item) => item.user_name.indexOf(dateRange.keyword) >= 0
            )
          );
        }
        if (dateRange.keywordType === 'user_phone') {
          setUserList(
            data?.filter((item) =>
              item.user_phone
                ?.replace(/-/g, '')
                ?.includes(dateRange.keyword?.replace(/-/g, ''))
            )
          );
        }
      } else {
        setUserList(data);
      }
    } catch (e) {
      MessageAlert.error(e.message);
    }
  };
  /* 데이터 조회 */
  const getData2 = async () => {
    try {
      // JM페이 내역
      const { status, message, data } = await JmPayApi.getJmPays({
        join_tables: `["user"]`,
        ...(dateRange.startDate
          ? {
              start_date: moment(dateRange.startDate).startOf('day').unix(),
            }
          : {}),
        ...(dateRange.endDate
          ? {
              end_date: moment(dateRange.endDate).endOf('day').unix(),
            }
          : {}),
        // types: `["AUTO_CHARGE", "OVER_CHARGE", "MANUAL_CHARGE", "USE"]`,
      });

      // 주문내역
      const { status: orderStatus, data: orderData } = await OrderApi.getOrders(
        {
          join_tables: '["refund", "shop_information"]',
          payment_method: 'JM_PAY',
          ...(dateRange.startDate
            ? {
                start_date: moment(dateRange.startDate).startOf('day').unix(),
              }
            : {}),
          ...(dateRange.endDate
            ? {
                end_date: moment(dateRange.endDate).endOf('day').unix(),
              }
            : {}),
        }
      );

      // 쇼핑몰 주문내역
      const { status: shopOrderStatus, data: shopOrderData } =
        await ShopOrderApi.getShopOrders({
          payment_method: 'JM_PAY',
          ...(dateRange.startDate
            ? {
                start_date: moment(dateRange.startDate).startOf('day').unix(),
              }
            : {}),
          ...(dateRange.endDate
            ? {
                end_date: moment(dateRange.endDate).endOf('day').unix(),
              }
            : {}),
        });
      const { status: parcelStatus, data: parcelData } =
        await ParcelServiceApi.getParcelServices({
          join_tables: '["refund", "shop_information"]',
          payment_method: 'JM_PAY',
          ...(dateRange.startDate
            ? {
                start_date: moment(dateRange.startDate).startOf('day').unix(),
              }
            : {}),
          ...(dateRange.endDate
            ? {
                end_date: moment(dateRange.endDate).endOf('day').unix(),
              }
            : {}),
        });

      if (
        status !== 200 ||
        orderStatus !== 200 ||
        shopOrderStatus !== 200 ||
        parcelStatus !== 200
      )
        throw { message };

      const mapOrderData = new Map();
      const mapOrderCreatedData = new Map();
      const mapShopOrderData = new Map();

      orderData.forEach((order) => {
        mapOrderData.set(
          `${order.created_at}-${order.user_id}-${order.use_jm_pay}`,
          order
        );
        for (let i = 0; i < 5; i++) {
          mapOrderCreatedData.set(order.created_at + i, order.created_at);
        }
      });

      const newShopOrder = shopOrderData.map((shopOrder) => ({
        ...shopOrder,
        parcel_service: parcelData.filter(
          (parcel) => parcel.shop_order_id === shopOrder.shop_order_id
        ),
      }));
      newShopOrder.forEach((order) => {
        mapShopOrderData.set(
          `${order.created_at}-${order.user_id}-${order.use_jm_pay}`,
          order
        );
        for (let i = 0; i < 5; i++) {
          mapOrderCreatedData.set(order.created_at + i, order.created_at);
        }
      });

      const newData = data.map((jm) => {
        // jm페이가 사용된 주문 찾기
        const createdAt = mapOrderCreatedData.get(jm.created_at);
        const findOrder =
          mapOrderData.get(`${createdAt}-${jm.user_id}-${jm.price}`) ?? null;

        const findShopOrder =
          mapShopOrderData.get(`${createdAt}-${jm.user_id}-${jm.price}`) ??
          null;

        return {
          ...jm,
          issued_cash_bill: findOrder?.cash_bill_info ?? null,
          order_id: findOrder?.order_id,
          cancel_cash_bill:
            findOrder?.refund?.[0]?.refund_cash_bill_info ?? null,
          order_status: findOrder?.payment_state,
          refund_id: findOrder?.refund?.[0]?.refund_id,
          order_data: findOrder
            ? findOrder
            : findShopOrder
            ? findShopOrder
            : null,
          order_type: findOrder ? 'order' : findShopOrder ? 'shop_order' : null,
        };
      });

      if (dateRange.keyword) {
        if (dateRange.keywordType === 'user_name') {
          setJmpayList(
            newData.filter(
              (item) => item.user.user_name.indexOf(dateRange.keyword) >= 0
            )
          );
        }
        if (dateRange.keywordType === 'user_phone') {
          setJmpayList(
            newData.filter((item) =>
              item.user.user_phone
                ?.replace(/-/g, '')
                ?.includes(dateRange.keyword?.replace(/-/g, ''))
            )
          );
        }
      } else {
        setJmpayList(newData);
      }
    } catch (e) {
      MessageAlert.error(e.message);
    }
  };
  /* 데이터 조회 */
  const getData3 = async () => {
    try {
      const { status, message, data } = await InquiryApi.getInquiries({
        join_tables: `["user"]`,
        inquiry_sub_type: `JM_PAY_REFUND`,
        ...(dateRange.startDate
          ? {
              start_date: moment(dateRange.startDate).startOf('day').unix(),
            }
          : {}),
        ...(dateRange.endDate
          ? {
              end_date: moment(dateRange.endDate).endOf('day').unix(),
            }
          : {}),
      });
      if (status !== 200) throw { message };
      // setRefundList(data);
      if (dateRange.keyword) {
        if (dateRange.keywordType === 'user_name') {
          setRefundList(
            data.filter(
              (item) => item.user.user_name.indexOf(dateRange.keyword) >= 0
            )
          );
        }
        if (dateRange.keywordType === 'user_phone') {
          setRefundList(
            data.filter((item) =>
              item.user.user_phone
                ?.replace(/-/g, '')
                ?.includes(dateRange.keyword?.replace(/-/g, ''))
            )
          );
        }
      } else {
        setRefundList(data);
      }
    } catch (e) {
      MessageAlert.error(e.message);
    }
  };

  /**
   * 데이터 조회
   * --
   */
  const getJmPayList = async () => {
    setIsLoading(true);
    // setUserList(null);
    try {
      let result;

      // JM Pay 유저목록
      if (currentTab === 'user-list') {
        result = await UserApi.getUsers({
          filter: 'jm_pay_setting',
          join_tables: `["grade"]`,
        });
      }
      // JM Pay 사용이력
      else if (currentTab === 'history') {
        result = await JmPayApi.getJmPays({
          join_tables: `["user"]`,
          types: `["AUTO_CHARGE", "OVER_CHARGE", "MANUAL_CHARGE", "USE"]`,
        });
      }
      // JM Pay 결제취소 목록
      else if (currentTab === 'cancel') {
        result = await JmPayApi.getJmPays({
          join_tables: `["user"]`,
          types: `["CANCELLED"]`,
        });
      }
      // JM Pay 환불 목록
      else if (currentTab === 'refund') {
        result = await JmPayApi.getJmPays({
          join_tables: `["user"]`,
          types: `["REFUNDED"]`,
        });
      }
      // JM Pay 환불신청 목록
      else if (currentTab === 'refund_application') {
        result = await InquiryApi.getInquiries({
          join_tables: `["user"]`,
          inquiry_sub_type: `JM_PAY_REFUND`,
        });
      }

      const { status, message, data } = result;
      if (status !== 200) throw { message };

      setTimeout(() => {
        setUserList(data);
        setIsLoading(false);
      }, 1000);
    } catch (e) {
      setIsLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**
   * JM페이 결제 취소(실결제여부 파라미터로 처리)
   * --
   */
  const handleCancelPay = async () => {
    setIsLoading(true);
    try {
      const { status, message, data } = await JmPayApi.getJmPay();
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**
   * JM페이 환불(실결제여부 파라미터로 처리)
   * --
   */
  const handleRefundPay = async () => {
    setIsLoading(true);
    try {
      const { status, message, data } = await JmPayApi.getJmPay();
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**
   * JM페이 환불(실결제여부 파라미터로 처리)
   * --
   */
  const handleChargePay = async () => {
    setIsLoading(true);
    try {
      const { status, message, data } = await JmPayApi.getJmPay();
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 리뷰목록 조회
   * --------------------
   */
  const handleSaveAnswer = async (newValue) => {
    const fid = userList.findIndex(
      (item) => item.inquiry_id === newValue.inquiry_id
    );
    setUserList([
      ...userList.slice(0, fid),
      newValue,
      ...userList.slice(fid + 1),
    ]);
  };

  /**
   * 현금영수증 발급/취소
   * --
   * @param {string} t issue | cancel
   * @param {object}
   */
  const handleIssueCashBill = async (t, d) => {
    const confirm = (msg = '') => window.confirm(msg);
    try {
      setIsLoading(true);

      const submitType = t.toLocaleUpperCase();

      if (submitType === 'ISSUE') {
        if (!confirm('현금영수증을 발급하시겠습니까?')) return false;

        const {
          userInfoType,
          PHONE_NUMB,
          BIZ_NUMB,
          totalAmount,
          data: jmpay,
          ...rest
        } = cashBillInfo;
        const { status, data } = await issueCashbill({
          ...rest,
          userInfoType,
          orderNumb: `jm_${dayjs().unix()}`,
          totalAmount: `${totalAmount}`,
          taxAmount: `${
            Math.abs(totalAmount) - Math.floor(Math.abs(totalAmount) / 1.1)
          }`,
          userInfo: cashBillInfo[userInfoType]?.split('-')?.join(''),
        });

        if (status !== 200) {
          MessageAlert.info('현금영수증 발급 실패');
          return false;
        }

        if (data && data?.code !== 'A0200') {
          MessageAlert.warning(data?.data?.respMessage);
          return false;
        }

        const update = await updateOrder({
          order_id: d?.order_id,
          cash_bill_info: data?.data,
        });

        // order 업데이트 실패 처리
        if (update.status !== 200) {
          MessageAlert.info('현금영수증 정보 업데이트 실패');

          // 현금영수증 취소 처리
          await cancelCashbill({
            mid: POINT_STORE_ID,
            orgTradeKeyType: 'TID',
            orgTradeKey: data?.data?.tid,
          });
          return false;
        }

        const fid = jmpayList?.findIndex((jm) => jm.jm_pay_id === d?.jm_pay_id);

        setJmpayList((prev) => [
          ...prev.slice(0, fid),
          { ...jmpayList[fid], issued_cash_bill: data?.data },
          ...prev.slice(fid + 1),
        ]);
        MessageAlert.success('현금영수증 발급 성공');
        return true;
      } else if (submitType === 'CANCEL') {
        if (!confirm('현금영수증을 발급을 취소하시겠습니까?')) return false;

        const { status, data } = await cancelCashbill({
          mid: POINT_STORE_ID,
          orgTradeKeyType: 'TID',
          orgTradeKey: d?.issued_cash_bill?.tid,
        });

        if (status !== 200) {
          MessageAlert.info('현금영수증 취소 실패');
          return false;
        }

        if (data?.code !== 'A0200') {
          MessageAlert.warning(data?.data?.respMessage);
          return false;
        }

        const update = await updateRefund({
          refund_id: d?.refund_id,
          refund_cash_bill_info: data?.data,
        });

        // 환불 정보 업데이트 실패 처리
        if (update.status !== 200) {
          MessageAlert.info('현금영수증 정보 업데이트 실패');
          return false;
        }

        const fid = jmpayList?.findIndex((jm) => jm.jm_pay_id === d?.jm_pay_id);

        setJmpayList((prev) => [
          ...prev.slice(0, fid),
          { ...jmpayList[fid], cancel_cash_bill: data?.data },
          ...prev.slice(fid + 1),
        ]);
        MessageAlert.success('현금영수증 발급 취소 성공');
        return true;
      } else {
        return;
      }
    } catch (err) {
      console.error(`[handleIssueCashBill] Error !!`, err);
      MessageAlert.error(err);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  /* ===== HOOKS ===== */
  /* */
  useEffect(() => {
    setCurrentTab('user-list');
    getData();
    getData2();
    getData3();
  }, []);
  useEffect(() => {
    getData();
    getData2();
    getData3();
  }, [dateRange]);
  useEffect(() => {
    // currentTab && getJmPayList();
  }, [currentTab]);

  /* ===== RENDER ===== */
  return (
    <>
      <JmPayPresenter
        {...{
          userList,
          jmpayList,
          refundList,
          currentTab,
          setCurrentTab,
          permission,
          statisticsType,
          isLoading,
          searchKeyword,
          setCurrentShop,
          setSearchKeyword,
          startDate,
          endDate,
          currentRow,
          setCurrentRow,
          onSearch: handleSearch,
          onSelect: setCurrentRow,
          onCancelPay: handleCancelPay,
          onSaveAnswer: handleSaveAnswer,
          onIssue: handleIssueCashBill,
          cashBillInfo,
          setCashBillInfo,
          initialValue: INITIAL_CASH_BILL,
        }}
      />
    </>
  );
};

/* DF */
JmPayContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
};

export default JmPayContainer;
